import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Paper, Alert, CircularProgress } from '@mui/material';
import CouponForm from './CouponForm';
import { CouponBase } from '../../../types/coupon';
import api from '../../../services/api';

const initialCoupon: Partial<CouponBase> = {
    code: '',
    title: '',
    description: '',
    design: {
        name: 'New Coupon',
        canvas: {
            backgroundColor: '#ffffff',
            breakpoints: {
                desktop: { width: 800, height: 400 },
                tablet: { width: 600, height: 300 },
                mobile: { width: 400, height: 200 }
            }
        },
        elements: [],
        assets: []
    },
    marketingSettings: {
        listId: process.env.REACT_APP_DEFAULT_MARKETING_LIST_ID || '40',
        partnerName: process.env.REACT_APP_DEFAULT_PARTNER_NAME || 'QRPon'
    },
    discountType: 'percentage',
    discountValue: 0,
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    validFrom: new Date().toISOString(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    minPurchaseAmount: 0,
    maxRedemptions: 100,
    status: 'draft',
    isActive: false
};

const CouponFormContainer: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [coupon, setCoupon] = useState<Partial<CouponBase> | null>(null);
    const [userId, setUserId] = useState<string | null>(null);

    useEffect(() => {
        // Get current user ID
        api.get('/auth/me')
            .then(response => {
                if (response.data?.data?.user?._id) {
                    setUserId(response.data.data.user._id);
                }
            })
            .catch(err => {
                console.error('Error getting user ID:', err);
            });
    }, []);

    useEffect(() => {
        if (id) {
            setLoading(true);
            api.get(`/admin/coupons/${id}`)
                .then(response => {
                    if (response.data?.data?.coupon) {
                        setCoupon(response.data.data.coupon);
                    } else {
                        setError('Invalid coupon data received');
                    }
                })
                .catch(err => {
                    console.error('Error loading coupon:', err);
                    setError(err.response?.data?.message || 'Failed to load coupon');
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setCoupon(initialCoupon);
        }
    }, [id]);

    const handleSubmit = async (formData: Partial<CouponBase>, goToDesign: boolean = false) => {
        console.log('CouponFormContainer handleSubmit called', { goToDesign });
        try {
            if (!userId) {
                console.error('No userId available');
                throw new Error('User ID not available');
            }

            setLoading(true);
            setError(null);

            // Prepare the data
            const submissionData = {
                ...formData,
                createdBy: userId,
                validStartDate: formData.validFrom,
                validEndDate: formData.validUntil
            };
            console.log('Preparing to submit data:', submissionData);
            
            const response = id
                ? await api.patch(`/admin/coupons/${id}`, submissionData)
                : await api.post('/admin/coupons', submissionData);

            console.log('API response:', response.data);

            if (response.data?.data?.coupon) {
                const savedCoupon = response.data.data.coupon;
                console.log('Coupon saved successfully:', savedCoupon);
                if (savedCoupon._id) {
                    if (goToDesign) {
                        console.log('Navigating to design page');
                        navigate(`/admin/coupons/${savedCoupon._id}/design`);
                    } else {
                        console.log('Refreshing coupon data');
                        setLoading(true);
                        try {
                            const refreshResponse = await api.get(`/admin/coupons/${savedCoupon._id}`);
                            if (refreshResponse.data?.data?.coupon) {
                                setCoupon(refreshResponse.data.data.coupon);
                                setError(null);
                            }
                        } catch (refreshErr: any) {
                            console.error('Error refreshing coupon:', refreshErr);
                            setError('Changes saved but failed to refresh data');
                        } finally {
                            setLoading(false);
                        }
                    }
                } else {
                    console.log('Navigating to coupons list');
                    navigate('/admin/coupons');
                }
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (err: any) {
            console.error('Error in handleSubmit:', err);
            console.error('Full error object:', {
                message: err.message,
                response: err.response,
                stack: err.stack
            });
            setError(err.response?.data?.message || 'Failed to save coupon');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Paper sx={{ p: 3, my: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <CouponForm
                    initialData={id && coupon ? coupon : undefined}
                    onSubmit={(formData, goToDesign) => handleSubmit(formData, goToDesign)}
                    onDesign={() => id && navigate(`/admin/coupons/${id}/design`)}
                />
            </Paper>
        </Container>
    );
};

export default CouponFormContainer;
